import type { Member } from '@rp/contracts'
import { leavesNoAdmin } from '@rp/contracts'
import { Badge, Button, Chip, EmptyState } from '../../design'
import { SESSION_COPY } from '../../session/copy'
import { SETTINGS_COPY } from './copy'
import { MEMBER_PASSWORD_COPY } from './passwordCopy'

export type MemberAction = 'role' | 'remove' | 'password'

type MemberListProps = {
  members: readonly Member[]
  selfId: string
  onAction: (action: MemberAction, member: Member) => void
}

type MemberRowProps = {
  member: Member
  isSelf: boolean
  soleAdmin: boolean
  onAction: (action: MemberAction) => void
}

const initialOf = (member: Member): string => (member.displayName ?? member.email).slice(0, 1)

const MemberRow = ({ member, isSelf, soleAdmin, onAction }: MemberRowProps) => (
  <li className="member" data-admin={String(member.role === 'admin')}>
    <span className="member-avatar" aria-hidden="true">
      {initialOf(member)}
    </span>
    <span className="member-identity">
      <span className="member-name">
        {member.displayName ?? member.email}
        {isSelf ? <Badge tone="neutral">{SETTINGS_COPY.you}</Badge> : null}
      </span>
      <span className="member-meta num">{member.email}</span>
    </span>
    <Chip variant={member.role === 'admin' ? 'solid' : 'outline'} tone="neutral">
      {SESSION_COPY.roleLabels[member.role]}
    </Chip>
    {soleAdmin ? <p className="member-note">{SETTINGS_COPY.lastAdmin}</p> : null}
    <span className="member-actions">
      <Button onClick={() => onAction('role')}>{SETTINGS_COPY.changeRole}</Button>
      {isSelf ? null : <Button onClick={() => onAction('password')}>{MEMBER_PASSWORD_COPY.reset}</Button>}
      <Button variant="danger" disabled={soleAdmin} onClick={() => onAction('remove')}>
        {SETTINGS_COPY.remove}
      </Button>
    </span>
  </li>
)

export const MemberList = ({ members, selfId, onAction }: MemberListProps) => {
  if (members.length === 0) return <EmptyState title={SETTINGS_COPY.membersEmpty} />

  return (
    <ul className="member-list" aria-label={SETTINGS_COPY.members}>
      {members.map((member) => (
        <MemberRow
          key={member.userId}
          member={member}
          isSelf={member.userId === selfId}
          soleAdmin={leavesNoAdmin(members, { userId: member.userId, role: null })}
          onAction={(action) => onAction(action, member)}
        />
      ))}
    </ul>
  )
}
